import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { SectionPager } from 'app/components/section-pager/section-pager';
import { MetroLine } from 'app/components/metro-line/metro-line';
import { ViewMode, WithMetroLineProps, WithMetroLineWrappedComponentProps, Configuration } from 'app/components/metro-line/types';
import { RefObject } from 'react';
import styles from './with-metro-line.scss';
import { scrollIntoView } from 'app/infrastructure/platform/browser-dom';
import { getSectionId, redirectToInvalidSection, isTabsMode } from 'app/components/metro-line/helpers';

interface MetroLineState<SectionType> {
  activeMode: ViewMode;
  activeSection: SectionType;
}

//eslint:disable:max-func-body-length
export const withMetroLine = <P, SectionType>() => (
  WrappedComponent: React.ComponentClass<P & WithMetroLineWrappedComponentProps<SectionType>> | React.FC<React.PropsWithChildren<P>>
) => {
  return class extends React.Component<P & WithMetroLineProps<SectionType>, MetroLineState<SectionType>> {

    private scrollToSectionHandler: number = null;

    public state: MetroLineState<SectionType> = {
      activeMode: ViewMode.scroll,
      activeSection: undefined
    };

    public componentDidMount() {
      const { configuration, initialMode, initialSection, forceMode } = this.props;

      window.addEventListener('scroll', this.handleScroll);

      if (!!initialSection && !!configuration && !!configuration.find(item => item.section === initialSection)) {
        this.setState({ activeMode: ViewMode.tabs, activeSection: initialSection });
      } else if (initialMode) {
        this.setState({ activeMode: initialMode });
      }

      if (!initialSection && !!configuration && configuration.length > 0) {
        this.setState({ activeSection: configuration[0].section });
      }

      if (!!forceMode) {
        this.setState({ activeMode: forceMode });
      }
    }

    public componentDidUpdate(prevProps: WithMetroLineProps<SectionType>) {
      if (prevProps.invalidSection !== this.props.invalidSection) {

        redirectToInvalidSection(this.props.invalidSection);

        this.markInvalidSectionAsActive(this.props.invalidSection);
      }
    }

    public componentWillUnmount() {
      window.removeEventListener('scroll', this.handleScroll);
      window.clearTimeout(this.scrollToSectionHandler);
      this.scrollToSectionHandler = null;
    }

    private markInvalidSectionAsActive = (invalidSection: Configuration<SectionType>) => {
      invalidSection && invalidSection.section && isTabsMode() && this.updateActiveSection(invalidSection.section);
    };

    private handleScroll = () => {
      const fromTop = window.scrollY;

      this.props.configuration.forEach(configurationItem => {

        const sectionRef = configurationItem.ref || null;

        const section = !!sectionRef && !!sectionRef.current ? sectionRef.current : null;

        if (!section) {
          return;
        }

        const offset = this.props.hasBigStickyHeader ? 47 : 16;

        if (
          section.offsetTop - offset <= fromTop &&
          section.offsetTop + section.offsetHeight > fromTop
        ) {
          if (this.state.activeSection !== configurationItem.section) {
            this.setState({ activeSection: configurationItem.section });
          }
        }
      });
    };

    private changeViewMode = (): void => {
      const { activeMode: mode, activeSection } = this.state;
      const { scrollViewUrl, configuration } = this.props;

      const newMode = mode === ViewMode.scroll ? ViewMode.tabs : ViewMode.scroll;
      this.setState({ activeMode: newMode });

      const currentConfiguration = configuration.find(configurationItem => configurationItem.section === activeSection);

      if (newMode === ViewMode.scroll) {
        navigate.toMetroLineSection(scrollViewUrl);
        if (this.scrollToSectionHandler !== null) {
          window.clearTimeout(this.scrollToSectionHandler);
          this.scrollToSectionHandler = null;
        }

        this.scrollToSectionHandler = window.setTimeout(() => {
          const anchor = document.getElementById(getSectionId(currentConfiguration.section));
          scrollIntoView(anchor, { behavior: 'smooth' });
        }, 500);

      } else {
        !!currentConfiguration && !!currentConfiguration.url && navigate.toMetroLineSection(currentConfiguration.url);
      }
    };

    private updateActiveSection = (activeSection: SectionType) => {
      this.setState({ activeSection });
    };

    private getNextSection = () => {
      const { configuration } = this.props;
      const currentSectionPosition = configuration.findIndex(configurationItem =>
        configurationItem.section === this.state.activeSection);
      const nextSection = configuration[currentSectionPosition + 1];
      return !!nextSection
        ? {
          id: undefined,
          label: nextSection.label(this.props.translation)
        }
        : undefined;
    };

    private getPreviousSection = () => {
      const { configuration } = this.props;
      const currentSectionPosition = configuration.findIndex(configurationItem =>
        configurationItem.section === this.state.activeSection);
      const previousSection = configuration[currentSectionPosition - 1];
      return !!previousSection
        ? {
          id: undefined,
          label: previousSection.label(this.props.translation)
        }
        : undefined;
    };

    private goToNextSection = (event: React.MouseEvent<any>) => {
      const { configuration } = this.props;
      event.preventDefault();

      this.removeFocus();

      const currentSectionPosition = configuration.findIndex(configurationItem =>
        configurationItem.section === this.state.activeSection);
      const nextSection = configuration[currentSectionPosition + 1];

      !!nextSection && this.setState({ activeSection: nextSection.section });
      !!nextSection && navigate.toMetroLineSection(nextSection.url);
    };

    private goToPreviousSection = (event: React.MouseEvent<any>) => {
      const { configuration } = this.props;
      event.preventDefault();

      this.removeFocus();

      const currentSectionPosition = configuration.findIndex(configurationItem =>
        configurationItem.section === this.state.activeSection);
      const previousSection = configuration[currentSectionPosition - 1];

      !!previousSection && this.setState({ activeSection: previousSection.section });
      !!previousSection && navigate.toMetroLineSection(previousSection.url);
    };

    private removeFocus = () => (document.activeElement as any).blur();

    private getRef = (section: SectionType): RefObject<HTMLDivElement | null> => {
      const sectionConfiguration = this.props.configuration.find(configurationItem => configurationItem.section === section);
      return !!sectionConfiguration ? sectionConfiguration.ref : undefined;
    };

    private shouldRenderSection = (currentSection: SectionType, sectionToRender: SectionType) => {
      return !currentSection || !!currentSection && currentSection === sectionToRender;
    };

    public render() {

      const { configuration, translation, scrollViewUrl, forceMode, label, validation, invalidSection } = this.props;
      const { activeSection, activeMode } = this.state;

      if (!configuration) {
        return null;
      }

      return <>
        <MetroLine
          invalidSection={invalidSection}
          configuration={configuration}
          translation={translation}
          scrollViewUrl={scrollViewUrl}
          updateActiveSection={this.updateActiveSection}
          changeViewMode={this.changeViewMode}
          activeSection={activeSection}
          activeMode={activeMode}
          forceMode={forceMode}
          label={label}
          validation={validation}
        />
        <div id="wrapped-component" className={styles.wrappedComponent}>
          <WrappedComponent {...this.props} getRef={this.getRef} shouldRenderSection={this.shouldRenderSection} />
        </div>
        <div>
          {this.state.activeMode === ViewMode.tabs
            ? <SectionPager
              hide={false}
              nextSection={this.getNextSection()}
              previousSection={this.getPreviousSection()}
              onNext={this.goToNextSection}
              onPrevious={this.goToPreviousSection}
              children={undefined}
            />
            : null
          }
        </div>
      </>;
    }
  };
};
